package com.crowpay.views.components

import com.crowpay.Specification
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.contractor
import com.crowpay.edit
import com.crowpay.extensions.withSpacing
import com.crowpay.sdk.notNullSession
import com.crowpay.trash
import com.crowpay.utils.pushChanges
import com.crowpay.utils.renderDollars
import com.crowpay.utils.resize
import com.crowpay.utils.truncate
import com.crowpay.views.components.files.FilePreview
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.dialogs.NewSpecViewDialog
import com.crowpay.views.theming.SectionSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.tertiaryButton
import com.lightningkite.UUID
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.models.times
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.Screen
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.reactive.BasicListenable
import com.lightningkite.kiteui.reactive.LazyPropertySuspending
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.await
import com.lightningkite.kiteui.reactive.awaitOnce
import com.lightningkite.kiteui.reactive.debounce
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.modify
import com.lightningkite.kiteui.reactive.reactiveSuspending
import com.lightningkite.kiteui.reactive.rerunOn
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.atStart
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.RowOrCol
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.rowCollapsingToColumn
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.forEach
import com.lightningkite.kiteui.views.italic
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.kiteui.views.withSpacing
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq

class EditSpecifications(
    val contractorId: UUID,
    val onSave: suspend (Specification) -> Unit = {}
) : Page {
    val _specifications = shared {
        notNullSession().specifications.query(
            Query(
                condition { it.contractor eq contractorId },
                limit = Int.MAX_VALUE
            )
        )
    }

    val forms = LazyPropertySuspending {
        _specifications()().map { spec -> createForm(spec) }
    }

    val specGroups = shared {
        forms()
            .groupBy { it.draft().group }
            .toList()
            .sortedBy { it.first?.name }
    }

    fun createForm(spec: Specification? = null): EditSpecificationItem {
        return EditSpecificationItem(
            ogSpec = spec,
            contractorId = contractorId,
            onRemove = { formId ->
                forms.value = forms.value.filter { it.draft()._id != formId }
            },
            onSave = onSave
        )
    }

    suspend fun hasUnsavedChanges(): Boolean {
        return forms.awaitOnce().any { form ->
            val files = form.files.awaitOnce()
            val og = form.draft.awaitOnce()
            form.draft.changesMade.await() ||
                    og.files.size != files.size ||
                    files.any { it.preview is FilePreview.LocalFile }
        }
    }

    val orderEdited = BasicListenable()
    var justAdded: UUID? = null

    override fun ViewWriter.render2(): ViewModifiable = stack {
        spacing = AppDimensions.sectionIndent
        col {
            var initialLoad = true
            reactiveSuspending {
                rerunOn(orderEdited.debounce(800))
                if (initialLoad) {
                    initialLoad = false
                    return@reactiveSuspending
                }
            }
            space(0.2)
            (if (viewIsWide) atEnd else ViewWrapper) - rowCollapsingToColumn(AppDimensions.rerenderWidth) {
                row {
                    fun ViewWriter.expandIfNarrow() = if (viewIsNarrow) expanding else ViewWrapper
                    expandIfNarrow() - secondaryButton - button {
                        specCenteredText("+ Spec Set")
                        onClick {
                            dialogScreenNavigator.navigate(
                                NewSpecViewDialog(
                                    options = forms().map { it.draft() },
                                    isSet = true
                                )
                            )
                        }
                    }
                    expandIfNarrow() - secondaryButton - button {
                        specCenteredText("+ Spec View")
                        onClick {
                            dialogScreenNavigator.navigate(
                                NewSpecViewDialog(
                                    options = forms().map { it.draft() },
                                    isSet = false
                                )
                            )
                        }
                    }
                }
                tertiaryButton - button {
                    specCenteredText("+ New Specification")
                    onClick {
                        val newForm = createForm()
                        forms.value += newForm
                        justAdded = newForm.draft()._id
                        newForm.expanded.value = true
                    }
                }
            }
            space()

            col {
                spacing = theme.spacing * 1.2
                forEach(specGroups) { (group, specs) ->
                    fun ViewWriter.renderForms(): RowOrCol =
                        col {
                            spacing = 0.rem
                            for (spec in specs) {
                                spec.render(this)
                                greySeparator()
                            }
                        }

                    if (group != null) {
                        compact - col {
                            val expanded = Property(true)
                            lightSection - row {
                                spacing = 0.px
                                dynamicTheme {
                                    if (expanded()) SectionSemantic else null
                                }
                                expanding - leftExpandButton(expanded, highlightOnExpand = false) {
                                    if (group.isSet) centered - scopeSetIcon(1.3.rem)
                                    (if (viewIsNarrow) expanding else ViewWrapper) - centered - bodyBold(group.name)
                                    if (viewIsWide) {
                                        space(0.1)
                                        expanding - centered - italic - smallBody(group.description?.truncate(50) ?: "")
                                    }
                                    centered - body {
                                        ::visible { specs.sumOf { it.draft().price ?: 0L } > 0 }
                                        ::content { specs.sumOf { it.draft().price ?: 0L }.renderDollars() }
                                    }
                                }
                                space()
                                padded - row {
                                    spacing = AppDimensions.expandButtonSpace
                                    button {
                                        spacing = 2.dp
                                        centered - icon(Icon.edit.resize(1.2.rem), "Edit Group")
                                        onClick {
                                            println("edit group")
                                        }
                                    }
                                    button {
                                        spacing = 2.dp
                                        centered - icon(Icon.trash.resize(1.2.rem), "Delete Group")
                                        onClick {
                                            dialogScreenNavigator.navigate(
                                                GenericConfirmationDialog(
                                                    "Delete ${group.name}?",
                                                    "This cannot be undone.",
                                                    confirmationText = "Delete"
                                                ) { confirmed ->
                                                    if (!confirmed) return@GenericConfirmationDialog
                                                    forms().forEach { form ->
                                                        val draft = form.draft()
                                                        if (draft.group?.name == group.name) form.draft.modify {
                                                            it.copy(
                                                                group = null,
                                                            )
                                                        }
                                                        form.draft.pushChanges { notNullSession().specifications }
                                                    }
                                                }
                                            )
                                        }
                                    }
                                }
                            }
                            onlyWhen { expanded() } - renderForms()
                        }
                    } else {
                        renderForms()
                    }
                }
            }
        }
    }
}